import { FC, useMemo, useCallback } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  isBefore,
  isAfter,
  format,
  startOfDay,
} from 'date-fns';
import { cn } from '../../utils/cn';
import { CalendarDay } from './CalendarDay';
import { CalendarGridProps, AvailabilityStatus, DateAvailabilityInfo } from './types';
import { Spinner } from '../ui/Spinner';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Threshold for "limited" availability (3 or fewer slots)
const LIMITED_SLOTS_THRESHOLD = 3;

export const CalendarGrid: FC<CalendarGridProps> = ({
  currentDate,
  selectedDate,
  availableDatesInfo,
  onDateSelect,
  minDate,
  maxDate,
  isLoading = false,
}) => {
  const today = startOfDay(new Date());

  // Generate all days to display in the calendar grid
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentDate]);

  // Create a Map for O(1) lookup of date availability info
  const availabilityMap = useMemo(() => {
    const map = new Map<string, DateAvailabilityInfo>();
    for (const info of availableDatesInfo) {
      map.set(info.date, info);
    }
    return map;
  }, [availableDatesInfo]);

  // Determine the status of each day based on real slot counts
  const getDateStatus = useCallback(
    (date: Date): AvailabilityStatus => {
      const dateStr = format(date, 'yyyy-MM-dd');

      // Past dates
      if (isBefore(date, today)) {
        return 'past';
      }

      // Before min date or after max date
      if (isBefore(date, minDate) || isAfter(date, maxDate)) {
        return 'unavailable';
      }

      // Check availability info from API
      const availability = availabilityMap.get(dateStr);

      if (!availability || availability.availableSlots === 0) {
        return 'unavailable';
      }

      // Limited availability: 3 or fewer slots available
      if (availability.availableSlots <= LIMITED_SLOTS_THRESHOLD) {
        return 'limited';
      }

      return 'available';
    },
    [today, minDate, maxDate, availabilityMap]
  );

  const isDateDisabled = (date: Date): boolean => {
    const status = getDateStatus(date);
    return status === 'past' || status === 'unavailable';
  };

  return (
    <div className="relative">
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/70 dark:bg-slate-900/70 flex items-center justify-center z-10 rounded-lg">
          <Spinner size="md" color="primary" label="Loading availability..." />
        </div>
      )}

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            className={cn(
              'text-center text-xs font-medium text-gray-500 dark:text-slate-400 py-2',
              day === 'Sun' && 'text-red-400 dark:text-red-500'
            )}
            aria-hidden="true"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar days grid */}
      <div className="grid grid-cols-7 gap-1" role="grid" aria-label="Calendar">
        {calendarDays.map((date) => {
          const dateStr = format(date, 'yyyy-MM-dd');
          const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
          const isToday = isSameDay(date, today);
          const isCurrentMonth = isSameMonth(date, currentDate);
          const status = getDateStatus(date);
          const disabled = isDateDisabled(date);

          return (
            <CalendarDay
              key={dateStr}
              date={date}
              isSelected={isSelected}
              isToday={isToday}
              isCurrentMonth={isCurrentMonth}
              status={status}
              onClick={() => onDateSelect(date)}
              disabled={disabled}
            />
          );
        })}
      </div>
    </div>
  );
};

CalendarGrid.displayName = 'CalendarGrid';
